import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip,
  Tooltip,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  Email,
  NotificationImportant,
  PictureAsPdf,
  Visibility,
  Block,
  CheckCircle,
  Search,
  Clear,
  AttachMoney,
} from '@mui/icons-material';
import { DataTable, StatusChip, FormModal } from '../components';
import PaymentDialog from '../components/PaymentDialog';
import api from '../services/api';

// Florida counties for tax calculation
const FLORIDA_COUNTIES = [
  'Alachua', 'Baker', 'Bay', 'Bradford', 'Brevard', 'Broward', 'Calhoun',
  'Charlotte', 'Citrus', 'Clay', 'Collier', 'Columbia', 'DeSoto', 'Dixie',
  'Duval', 'Escambia', 'Flagler', 'Franklin', 'Gadsden', 'Gilchrist', 'Glades',
  'Gulf', 'Hamilton', 'Hardee', 'Hendry', 'Hernando', 'Highlands', 'Hillsborough',
  'Holmes', 'Indian River', 'Jackson', 'Jefferson', 'Lafayette', 'Lake', 'Lee',
  'Leon', 'Levy', 'Liberty', 'Madison', 'Manatee', 'Marion', 'Martin', 'Miami-Dade',
  'Monroe', 'Nassau', 'Okaloosa', 'Okeechobee', 'Orange', 'Osceola', 'Palm Beach',
  'Pasco', 'Pinellas', 'Polk', 'Putnam', 'St. Johns', 'St. Lucie', 'Santa Rosa',
  'Sarasota', 'Seminole', 'Sumter', 'Suwannee', 'Taylor', 'Union', 'Volusia',
  'Wakulla', 'Walton', 'Washington',
];

// County tax rates (simplified - would need accurate rates in production)
const COUNTY_TAX_RATES = {
  'Miami-Dade': 0.07,
  'Broward': 0.07,
  'Palm Beach': 0.07,
  'Hillsborough': 0.075,
  'Pinellas': 0.07,
  'Orange': 0.065,
  // Default for other counties
  default: 0.06,
};

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalOutstanding: 0,
    totalOverdue: 0,
    paidThisMonth: 0,
  });

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    estate_sale_id: '',
    tax_county: '',
    notes: '',
    terms: 'Net 30',
    due_date: '',
    line_items: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchClients();
    fetchSales();
    fetchStats();
  }, [statusFilter, startDate, endDate, showOverdueOnly]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {};

      if (statusFilter !== 'all') params.status = statusFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const endpoint = showOverdueOnly ? '/invoices/overdue' : '/invoices';
      const response = await api.get(endpoint, { params });
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      showSnackbar('Failed to load invoices', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchSales = async () => {
    try {
      const response = await api.get('/sales');
      setSales(response.data?.sales || response.data || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/invoices');
      const allInvoices = response.data || [];

      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const outstanding = allInvoices
        .filter(inv => ['sent', 'partially_paid', 'overdue'].includes(inv.status))
        .reduce((sum, inv) => sum + (inv.total_amount - inv.amount_paid), 0);

      const overdue = allInvoices
        .filter(inv => inv.status === 'overdue')
        .reduce((sum, inv) => sum + (inv.total_amount - inv.amount_paid), 0);

      const paidThisMonth = allInvoices
        .filter(inv => {
          if (inv.status !== 'paid') return false;
          const updatedDate = new Date(inv.updated_at || inv.created_at);
          return updatedDate >= firstDayOfMonth;
        })
        .reduce((sum, inv) => sum + inv.total_amount, 0);

      setStats({
        totalOutstanding: outstanding,
        totalOverdue: overdue,
        paidThisMonth,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const calculateLineTotals = (lineItems) => {
    let subtotal = 0;
    let taxableAmount = 0;

    lineItems.forEach(item => {
      const lineTotal = item.quantity * item.unit_price;
      subtotal += lineTotal;
      if (item.taxable) {
        taxableAmount += lineTotal;
      }
    });

    const taxRate = formData.tax_county
      ? (COUNTY_TAX_RATES[formData.tax_county] || COUNTY_TAX_RATES.default)
      : 0;

    const taxAmount = taxableAmount * taxRate;
    const total = subtotal + taxAmount;

    return { subtotal, taxAmount, total };
  };

  const handleAddLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        {
          description: '',
          quantity: 1,
          unit_price: 0,
          taxable: true,
          inventory_item_id: null,
        },
      ],
    });
  };

  const handleRemoveLineItem = (index) => {
    const newLineItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: newLineItems });
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    setFormData({ ...formData, line_items: newLineItems });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.client_id) errors.client_id = 'Client is required';
    if (!formData.estate_sale_id) errors.estate_sale_id = 'Estate sale is required';
    if (!formData.tax_county) errors.tax_county = 'Tax county is required';
    if (!formData.due_date) errors.due_date = 'Due date is required';
    if (formData.line_items.length === 0) errors.line_items = 'At least one line item is required';

    formData.line_items.forEach((item, index) => {
      if (!item.description?.trim()) {
        errors[`line_item_${index}_description`] = 'Description is required';
      }
      if (item.quantity <= 0) {
        errors[`line_item_${index}_quantity`] = 'Quantity must be greater than 0';
      }
      if (item.unit_price < 0) {
        errors[`line_item_${index}_unit_price`] = 'Price cannot be negative';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateInvoice = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const { subtotal, taxAmount, total } = calculateLineTotals(formData.line_items);

      const invoiceData = {
        client_id: formData.client_id,
        estate_sale_id: formData.estate_sale_id,
        tax_county: formData.tax_county,
        notes: formData.notes,
        terms: formData.terms,
        due_date: formData.due_date,
        subtotal,
        tax_amount: taxAmount,
        total_amount: total,
        amount_paid: 0,
        status: 'draft',
        line_items: formData.line_items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total: item.quantity * item.unit_price,
          taxable: item.taxable,
          inventory_item_id: item.inventory_item_id,
        })),
      };

      await api.post('/invoices', invoiceData);
      showSnackbar('Invoice created successfully', 'success');
      setCreateModalOpen(false);
      resetForm();
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error creating invoice:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to create invoice', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateInvoice = async (invoiceId, updates) => {
    try {
      await api.put(`/invoices/${invoiceId}`, updates);
      showSnackbar('Invoice updated successfully', 'success');
      fetchInvoices();
      fetchStats();
      if (selectedInvoice?.id === invoiceId) {
        const response = await api.get(`/invoices/${invoiceId}`);
        setSelectedInvoice(response.data);
      }
    } catch (error) {
      console.error('Error updating invoice:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to update invoice', 'error');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/send`);
      showSnackbar('Invoice sent successfully', 'success');
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error sending invoice:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to send invoice', 'error');
    }
  };

  const handleSendReminder = async (invoiceId) => {
    try {
      await api.post(`/invoices/${invoiceId}/remind`);
      showSnackbar('Reminder sent successfully', 'success');
    } catch (error) {
      console.error('Error sending reminder:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to send reminder', 'error');
    }
  };

  const handleDownloadPDF = async (invoiceId) => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('PDF downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showSnackbar('Failed to download PDF', 'error');
    }
  };

  const handleVoidInvoice = async (invoiceId) => {
    try {
      await api.delete(`/invoices/${invoiceId}`);
      showSnackbar('Invoice voided successfully', 'success');
      setDetailModalOpen(false);
      fetchInvoices();
      fetchStats();
    } catch (error) {
      console.error('Error voiding invoice:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to void invoice', 'error');
    }
  };

  const handleMarkPaid = async (invoiceId, totalAmount) => {
    try {
      await api.put(`/invoices/${invoiceId}`, {
        amount_paid: totalAmount,
        status: 'paid',
      });
      showSnackbar('Invoice marked as paid', 'success');
      fetchInvoices();
      fetchStats();
      if (selectedInvoice?.id === invoiceId) {
        const response = await api.get(`/invoices/${invoiceId}`);
        setSelectedInvoice(response.data);
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      showSnackbar('Failed to mark invoice as paid', 'error');
    }
  };

  const handleProcessPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentDialogOpen(true);
  };

  const handlePaymentSuccess = async (payment) => {
    showSnackbar('Payment processed successfully', 'success');
    setPaymentDialogOpen(false);
    fetchInvoices();
    fetchStats();
    if (detailModalOpen && selectedInvoice) {
      const response = await api.get(`/invoices/${selectedInvoice.id}`);
      setSelectedInvoice(response.data);
    }
  };

  const handleViewDetails = async (invoice) => {
    try {
      const response = await api.get(`/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
      setDetailModalOpen(true);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      showSnackbar('Failed to load invoice details', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      estate_sale_id: '',
      tax_county: '',
      notes: '',
      terms: 'Net 30',
      due_date: '',
      line_items: [],
    });
    setFormErrors({});
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusChipProps = (status) => {
    const statusMap = {
      draft: 'default',
      sent: 'info',
      paid: 'success',
      partially_paid: 'warning',
      overdue: 'error',
      voided: 'default',
    };
    return statusMap[status] || 'default';
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        invoice.invoice_number?.toLowerCase().includes(searchLower) ||
        invoice.client_name?.toLowerCase().includes(searchLower);

      return matchesSearch;
    });
  }, [invoices, searchQuery]);

  const columns = [
    {
      field: 'invoice_number',
      headerName: 'Invoice #',
      sortable: true,
      searchable: true,
      minWidth: 120,
    },
    {
      field: 'client_name',
      headerName: 'Client',
      sortable: true,
      searchable: true,
      minWidth: 180,
      render: (row) => row.client_name || 'Unknown Client',
    },
    {
      field: 'sale_name',
      headerName: 'Sale',
      sortable: true,
      minWidth: 180,
      render: (row) => row.sale_name || 'N/A',
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      sortable: true,
      align: 'right',
      minWidth: 100,
      render: (row) => `$${Number(row.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: 'amount_paid',
      headerName: 'Paid',
      sortable: true,
      align: 'right',
      minWidth: 100,
      render: (row) => `$${Number(row.amount_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      field: 'balance',
      headerName: 'Balance',
      sortable: true,
      align: 'right',
      minWidth: 100,
      render: (row) => {
        const balance = (row.total_amount || 0) - (row.amount_paid || 0);
        return `$${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: true,
      minWidth: 130,
      render: (row) => <StatusChip status={row.status} />,
    },
    {
      field: 'due_date',
      headerName: 'Due Date',
      sortable: true,
      minWidth: 120,
      render: (row) => row.due_date ? new Date(row.due_date).toLocaleDateString() : 'N/A',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'center',
      minWidth: 200,
      render: (row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => handleViewDetails(row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status !== 'voided' && (
            <>
              <Tooltip title="Send Invoice">
                <IconButton
                  size="small"
                  onClick={() => handleSendInvoice(row.id)}
                  disabled={row.status === 'paid'}
                >
                  <Email fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Reminder">
                <IconButton
                  size="small"
                  onClick={() => handleSendReminder(row.id)}
                  disabled={row.status === 'draft' || row.status === 'paid'}
                >
                  <NotificationImportant fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download PDF">
                <IconButton size="small" onClick={() => handleDownloadPDF(row.id)}>
                  <PictureAsPdf fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Stack>
      ),
    },
  ];

  const totals = useMemo(() => calculateLineTotals(formData.line_items), [formData.line_items, formData.tax_county]);

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Invoices & Billing
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage invoices and track payments
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            resetForm();
            setCreateModalOpen(true);
          }}
        >
          Create Invoice
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Outstanding
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                ${stats.totalOutstanding.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Overdue
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                ${stats.totalOverdue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Paid This Month
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                ${stats.paidThisMonth.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by invoice # or client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: searchQuery && (
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="partially_paid">Partially Paid</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                  <MenuItem value="voided">Voided</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showOverdueOnly}
                    onChange={(e) => setShowOverdueOnly(e.target.checked)}
                  />
                }
                label="Show Overdue Only"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <DataTable
        columns={columns}
        data={filteredInvoices}
        loading={loading}
        searchable={false}
        emptyMessage="No invoices found"
        defaultRowsPerPage={25}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Create Invoice Modal */}
      <FormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create Invoice"
        onSave={handleCreateInvoice}
        saveLabel="Create Invoice"
        loading={saving}
        maxWidth="lg"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!formErrors.client_id}>
              <InputLabel>Client *</InputLabel>
              <Select
                value={formData.client_id}
                label="Client *"
                onChange={(e) => {
                  setFormData({ ...formData, client_id: e.target.value });
                  setFormErrors({ ...formErrors, client_id: '' });
                }}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name} {client.email && `(${client.email})`}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.client_id && (
                <Typography variant="caption" color="error">
                  {formErrors.client_id}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!formErrors.estate_sale_id}>
              <InputLabel>Estate Sale *</InputLabel>
              <Select
                value={formData.estate_sale_id}
                label="Estate Sale *"
                onChange={(e) => {
                  setFormData({ ...formData, estate_sale_id: e.target.value });
                  setFormErrors({ ...formErrors, estate_sale_id: '' });
                }}
              >
                {sales.map((sale) => (
                  <MenuItem key={sale.id} value={sale.id}>
                    {sale.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.estate_sale_id && (
                <Typography variant="caption" color="error">
                  {formErrors.estate_sale_id}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!formErrors.tax_county}>
              <InputLabel>Tax County *</InputLabel>
              <Select
                value={formData.tax_county}
                label="Tax County *"
                onChange={(e) => {
                  setFormData({ ...formData, tax_county: e.target.value });
                  setFormErrors({ ...formErrors, tax_county: '' });
                }}
              >
                {FLORIDA_COUNTIES.map((county) => (
                  <MenuItem key={county} value={county}>
                    {county}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.tax_county && (
                <Typography variant="caption" color="error">
                  {formErrors.tax_county}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Due Date *"
              value={formData.due_date}
              onChange={(e) => {
                setFormData({ ...formData, due_date: e.target.value });
                setFormErrors({ ...formErrors, due_date: '' });
              }}
              error={!!formErrors.due_date}
              helperText={formErrors.due_date}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Payment Terms"
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Line Items</Typography>
              <Button
                startIcon={<Add />}
                onClick={handleAddLineItem}
                size="small"
              >
                Add Item
              </Button>
            </Box>

            {formErrors.line_items && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formErrors.line_items}
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description *</TableCell>
                    <TableCell width={100}>Qty *</TableCell>
                    <TableCell width={120}>Unit Price *</TableCell>
                    <TableCell width={80}>Taxable</TableCell>
                    <TableCell width={120}>Total</TableCell>
                    <TableCell width={60}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.line_items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={item.description}
                          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                          error={!!formErrors[`line_item_${index}_description`]}
                          placeholder="Item description"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          error={!!formErrors[`line_item_${index}_quantity`]}
                          inputProps={{ min: 0, step: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          error={!!formErrors[`line_item_${index}_unit_price`]}
                          inputProps={{ min: 0, step: 0.01 }}
                          InputProps={{ startAdornment: '$' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={item.taxable}
                          onChange={(e) => handleLineItemChange(index, 'taxable', e.target.checked)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        ${(item.quantity * item.unit_price).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveLineItem(index)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {formData.line_items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No line items added. Click "Add Item" to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totals */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ minWidth: 300 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    ${totals.subtotal.toFixed(2)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">
                    Tax ({formData.tax_county ? ((COUNTY_TAX_RATES[formData.tax_county] || COUNTY_TAX_RATES.default) * 100).toFixed(1) : 0}%):
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    ${totals.taxAmount.toFixed(2)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    ${totals.total.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </FormModal>

      {/* Invoice Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedInvoice && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Invoice {selectedInvoice.invoice_number}</Typography>
                <StatusChip status={selectedInvoice.status} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {/* Invoice Header */}
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Client</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedInvoice.client_name || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Estate Sale</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedInvoice.sale_name || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Due Date</Typography>
                  <Typography variant="body1">
                    {selectedInvoice.due_date ? new Date(selectedInvoice.due_date).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Tax County</Typography>
                  <Typography variant="body1">{selectedInvoice.tax_county || 'N/A'}</Typography>
                </Grid>
                {selectedInvoice.terms && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Terms</Typography>
                    <Typography variant="body1">{selectedInvoice.terms}</Typography>
                  </Grid>
                )}
                {selectedInvoice.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{selectedInvoice.notes}</Typography>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Line Items */}
              <Typography variant="h6" mb={2}>Line Items</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="center">Taxable</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedInvoice.line_items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">${Number(item.unit_price).toFixed(2)}</TableCell>
                        <TableCell align="center">
                          {item.taxable ? <CheckCircle fontSize="small" color="success" /> : '-'}
                        </TableCell>
                        <TableCell align="right">${Number(item.total || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ minWidth: 300 }}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ${Number(selectedInvoice.subtotal || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">Tax:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      ${Number(selectedInvoice.tax_amount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  {selectedInvoice.commission_amount > 0 && (
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Commission:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ${Number(selectedInvoice.commission_amount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body1" fontWeight="bold">Total Amount:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      ${Number(selectedInvoice.total_amount || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="success.main">Amount Paid:</Typography>
                    <Typography variant="body2" color="success.main" fontWeight="bold">
                      ${Number(selectedInvoice.amount_paid || 0).toFixed(2)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Balance Due:</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      ${(Number(selectedInvoice.total_amount || 0) - Number(selectedInvoice.amount_paid || 0)).toFixed(2)}
                    </Typography>
                  </Box>
                  {selectedInvoice.net_to_client && (
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      <Typography variant="body2" color="text.secondary">Net to Client:</Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight="bold">
                        ${Number(selectedInvoice.net_to_client || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setDetailModalOpen(false)}>Close</Button>
              {selectedInvoice.status !== 'voided' && (
                <>
                  <Button
                    startIcon={<PictureAsPdf />}
                    onClick={() => handleDownloadPDF(selectedInvoice.id)}
                  >
                    Download PDF
                  </Button>
                  <Button
                    startIcon={<Email />}
                    onClick={() => handleSendInvoice(selectedInvoice.id)}
                    disabled={selectedInvoice.status === 'paid'}
                  >
                    Send
                  </Button>
                  {selectedInvoice.status !== 'draft' && selectedInvoice.status !== 'paid' && (
                    <Button
                      startIcon={<NotificationImportant />}
                      onClick={() => handleSendReminder(selectedInvoice.id)}
                    >
                      Remind
                    </Button>
                  )}
                  {selectedInvoice.status !== 'paid' && (
                    <>
                      <Button
                        variant="contained"
                        startIcon={<AttachMoney />}
                        onClick={() => handleProcessPayment(selectedInvoice)}
                      >
                        Process Payment
                      </Button>
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => handleMarkPaid(selectedInvoice.id, selectedInvoice.total_amount)}
                      >
                        Mark Paid
                      </Button>
                    </>
                  )}
                  <Button
                    color="error"
                    startIcon={<Block />}
                    onClick={() => {
                      if (window.confirm('Are you sure you want to void this invoice?')) {
                        handleVoidInvoice(selectedInvoice.id);
                      }
                    }}
                  >
                    Void
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        invoice={selectedInvoice}
        onPaymentSuccess={handlePaymentSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Invoices;
